import React, { useState } from 'react';
import { LogOut, ChevronLeft, ChevronRight, Shield } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { SidebarItem } from '@/components/layout/SidebarItem';
import { SidebarContextSwitcher } from '@/components/layout/SidebarContextSwitcher';
import { type Office, type Department, type Organization } from '@/types/org';
import { type UserProfile } from '@/types/team';
import { type NavItem } from '@/config/navConfig';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { GamutLogo } from '@/components/ui/GamutLogo';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
interface SidebarProps {
    profile: UserProfile | null;
    organization: Organization | null;
    offices: Office[];
    departments: Department[];
    activeOfficeId: string | null;
    activeDepartmentId: string | null;
    setActiveDepartmentId: (id: string | null) => void;
    signOut: () => Promise<void>;
    navGroups: {
        primary: NavItem[];
        organize: NavItem[];
        measure: NavItem[];
        configure: NavItem[];
    };
    /**
     * WEB-111 increment 4: the phone drawer is the only place the Sidebar renders (there is no rail at any width since
     * increment 1), so it shows the full labels and no collapse toggle there.
     */
    expanded?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
    profile,
    organization,
    offices,
    departments,
    activeOfficeId,
    activeDepartmentId,
    setActiveDepartmentId,
    signOut,
    navGroups,
    expanded = false,
}) => {
    const location = useLocation();
    const [collapsedPreference, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem('sidebarCollapsed');
        if (saved) {
            return JSON.parse(saved);
        }
        // Default to collapsed below 1024px. Expanded the sidebar is a fixed 288px,
        // which on a 390px screen leaves the main column too narrow to render the
        // header row - the Create Job button ended up ~310px past the right edge with
        // no horizontal scroll, so it was unreachable rather than merely off-screen.
        // WEB-98 raised the threshold from 768: an 834px tablet with the rail open had
        // a 482px content column, phone-width, and the job header, the jobs list rows
        // and the dashboard KPI cards all overlapped in it. A saved choice still wins.
        return typeof window !== 'undefined' && window.innerWidth < 1024;
    });

    const isCollapsed = !expanded && collapsedPreference;

    const toggleCollapse = () => {
        setIsCollapsed((prev: boolean) => {
            const next = !prev;
            localStorage.setItem('sidebarCollapsed', JSON.stringify(next));
            return next;
        });
    };

    const renderNavSection = (items: NavItem[], title?: string) => {
        if (!items || items.length === 0) return null;
        return (
            <div className="mb-6">
                {title && !isCollapsed && (
                    <h3 className="px-4 text-xs font-bold text-text-muted uppercase tracking-wider mb-2">
                        {title}
                    </h3>
                )}
                <div className="space-y-0.5">
                    {items.map((item) => (
                        <SidebarItem
                            key={item.to}
                            icon={item.icon}
                            label={item.label}
                            to={item.to}
                            isActive={location.pathname === item.to || (!item.exact && item.to !== '/' && location.pathname.startsWith(item.to + '/'))}
                            isCollapsed={isCollapsed}
                        />
                    ))}
                </div>
            </div>
        );
    };

    let profileLink = '/profile';
    if (activeOfficeId && activeDepartmentId) {
        profileLink = `/office/${activeOfficeId}/department/${activeDepartmentId}/profile`;
    } else if (activeOfficeId) {
        profileLink = `/office/${activeOfficeId}/profile`;
    }

    return (
        <aside className={`shrink-0 border-r border-divider-subtle flex flex-col h-full z-30 bg-bg-primary/40 backdrop-blur-xl shadow-2xl isolate transition-all duration-300 ${isCollapsed ? 'w-20 p-4' : 'w-72 p-6'}`}>
            <div className="mb-8 flex w-full items-center justify-center">
                <GamutLogo alt="Gamut" className="w-20 h-20 object-contain shrink-0" />
            </div>

            <SidebarContextSwitcher
                organization={organization}
                offices={
                    (profile?.role === 'OWNER' || profile?.role === 'ORG_ADMIN')
                        ? offices
                        : offices.filter(o => o.id === profile?.officeId)
                }
                departments={departments}
                activeOfficeId={activeOfficeId}
                activeDepartmentId={activeDepartmentId}
                setActiveDepartmentId={setActiveDepartmentId}
                userRole={profile?.role}
                userProfile={profile}
                isCollapsed={isCollapsed}
            />

            <div className={`mb-6 flex items-center ${isCollapsed ? 'justify-center flex-col gap-4' : 'justify-between'}`}>
                <ThemeToggle />
                {!expanded && <button
                    onClick={toggleCollapse}
                    className="flex items-center justify-center p-3 rounded-xl transition-all duration-200 text-text-secondary hover:bg-surface-hover hover:text-text-primary group border border-divider-subtle bg-bg-primary/20 cursor-pointer"
                    title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                >
                    <div className="transition-transform duration-300 group-hover:scale-110 text-accent-electric">
                        {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                    </div>
                </button>}
            </div>

            <nav className={`flex-1 overflow-y-auto custom-scrollbar ${isCollapsed ? '-mx-2 px-2' : '-mx-4 px-4'}`}>
                {renderNavSection(navGroups.primary)}
                {renderNavSection(navGroups.organize, "Organize")}
                {renderNavSection(navGroups.measure, "Measure")}
                {renderNavSection(navGroups.configure, "Configure")}
            </nav>

            <div className={`mt-auto border-t border-divider-subtle pt-6 ${isCollapsed ? 'flex flex-col items-center' : ''}`}>
                <Link to={profileLink} className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'} mb-5 p-2 ${isCollapsed ? '' : '-mx-2'} rounded-xl transition-colors duration-200 hover:bg-surface-hover no-underline group cursor-pointer`} title={isCollapsed ? "Profile" : undefined}>
                    <div className="w-10 h-10 shrink-0 rounded-[10px] bg-bg-tertiary flex items-center justify-center text-sm font-semibold text-text-primary group-hover:text-accent-electric transition-colors overflow-hidden">
                        <UserAvatar user={profile} />
                    </div>
                    {!isCollapsed && (
                        <div className="overflow-hidden">
                            <div className="text-sm font-bold text-text-primary whitespace-nowrap overflow-hidden text-ellipsis group-hover:text-accent-electric transition-colors">
                                {profile?.displayName || 'User'}
                            </div>
                            <div className="text-xs text-text-muted capitalize">
                                {profile?.role.replace('_', ' ')}
                            </div>
                            {profile?.departmentId && (
                                <div className="text-[0.7rem] text-accent-electric font-medium mt-0.5">
                                    {departments.find(d => d.id === profile.departmentId)?.name}
                                </div>
                            )}
                        </div>
                    )}
                </Link>
                <button
                    onClick={signOut}
                    title={isCollapsed ? "Sign Out" : undefined}
                    className={`w-full flex items-center ${isCollapsed ? 'justify-center px-0' : 'gap-3 px-4'} py-3 rounded-xl text-[#ff4444] bg-transparent border-none transition-all duration-200 cursor-pointer hover:bg-[rgba(255,68,68,0.1)]`}
                >
                    <LogOut size={20} />
                    {!isCollapsed && <span className="font-medium">Sign Out</span>}
                </button>

                <Link
                    to="/privacy"
                    title={isCollapsed ? "Privacy Policy" : undefined}
                    className={`w-full flex items-center ${isCollapsed ? 'justify-center px-0' : 'gap-3 px-4'} py-3 mt-2 rounded-xl text-text-muted bg-transparent transition-all duration-200 cursor-pointer hover:bg-surface-hover hover:text-accent-electric no-underline`}
                >
                    <Shield size={20} />
                    {!isCollapsed && <span className="text-sm font-medium">Privacy Policy</span>}
                </Link>
            </div>
        </aside>
    );
};
